import React, { useEffect, useState } from 'react'
import { Smartico } from '../../../core/Smartico'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { RootState } from '../../../redux/store'
import { GetRelatedAchTourResponse } from '@smartico/public-api/dist/Missions/GetRelatedAchTourResponse'
import { getStatusClassName, missionStatus } from '../../Sections/MissionsSection/MissionsSection'
import { Link, useLocation } from 'react-router-dom'
import { statusClazz } from '../../Sections/TournamentsSection/TournametsSection'
import { useTranslation } from '../../../hooks/useTranslation'
import { TMissionOrBadge } from '@smartico/public-api/dist/WSAPI/WSAPITypes'
import { TTournament } from '@smartico/public-api/dist/WSAPI/WSAPITypes'
import Utils from '../../../core/Utils'
import SectionWrapper from '../../Sections/SectionWrapper/SectionWrapper'
import { LeaderboardTab } from '../../../pages/TournamentDetailsPage/TournamentDetailsPage'
import { fetchTournamentDetails } from '../../../redux/slices/tournamentDetailesSlice'
import { TournamentRegistrationStatusName } from '@smartico/public-api'

const NavigationTabs = ({
	activeList,
	setActiveList,
	missions,
	tournaments,
	isSidebarOpen,
	toggleSidebar,
	onBackAch,
	onBackTour,
}: {
	activeList: 'achievements' | 'tournaments'
	setActiveList: React.Dispatch<React.SetStateAction<'achievements' | 'tournaments'>>
	missions: TMissionOrBadge[]
	tournaments: TTournament[]
	isSidebarOpen: boolean
	toggleSidebar: (state: boolean) => void
	onBackAch: () => void
	onBackTour: () => void
}) => {
	const isMobile = Utils.isMobile()
	const totalMissions = missions.length
	const totalTournaments = tournaments.length
	const completed = missions.filter((m) => m.is_completed).length
	const completionRatio = totalMissions > 0 ? completed / totalMissions : 0
	const pct = completionRatio * 100

	return (
		<div className="navigation">
			<div
				className="progress-border"
				style={{
					background: `conic-gradient(from 0deg, #1DB01A 0% ${pct}%, #7B75C2 ${pct}% 100%)`,
				}}
			>
				<div
					className={`nav-item achievements ${activeList === 'achievements' ? 'active-tab' : ''}`}
					onClick={() => {
						toggleSidebar(true)
						setActiveList('achievements')
						if (activeList !== null) onBackAch()
					}}
				/>
				<div className="counter">{completed}</div>
			</div>

			<div
				className="progress-border"
				style={{
					background: '#7B75C2',
				}}
			>
				<div
					className={`nav-item tournaments ${activeList === 'tournaments' ? 'active-tab' : ''}`}
					onClick={() => {
						toggleSidebar(true)
						setActiveList('tournaments')
						if (activeList !== null) onBackTour()
					}}
				/>
				<div className="counter tournaments">{totalTournaments}</div>
			</div>

			{isMobile && isSidebarOpen && <div className="toggle-sidebar-btn" onClick={() => toggleSidebar(false)} />}
		</div>
	)
}

const MissionsTab = ({ filteredMissions, onSelectMission }: { filteredMissions: TMissionOrBadge[]; onSelectMission: (mission: TMissionOrBadge) => void }) => {
	return (
		<>
			{filteredMissions.map((mission) => (
				<div className={`mission-card ${getStatusClassName(mission)}`} key={mission.id} onClick={() => onSelectMission(mission)}>
					{getStatusClassName(mission) && <div className={`mission-status ${getStatusClassName(mission)}`}>{missionStatus(mission)}</div>}
					<div className="wrap-wrap-wrap">
						<div className="mission-image" style={{ backgroundImage: `url(${mission.image})` }} />
						<div className="mission-title" dangerouslySetInnerHTML={{ __html: mission.name || '' }}></div>
					</div>
					<div className={`mission-progress ${getStatusClassName(mission)}`}>
						<div className="progress-bar">
							<div className="progress-fill" style={{ width: `${mission.progress}%` }}></div>
						</div>
					</div>
				</div>
			))}
		</>
	)
}

const MissionDetails = ({ mission }: { mission: TMissionOrBadge }) => {
	const { getTexts } = useTranslation(true)
	const { is_locked: isLocked, is_completed: isCompletedMission, is_opted_in: isOptedIn, is_requires_optin: isRequiresOptin, cta_action, cta_text } = mission
	const needOptin = isRequiresOptin && !isOptedIn && !isLocked
	return (
		<div className="mission-detail-container">
			<div className={`mission-card ${getStatusClassName(mission)}`} key={mission.id}>
				{getStatusClassName(mission) && <div className={`mission-status ${getStatusClassName(mission)}`}>{missionStatus(mission)}</div>}
				<div className="wrap-wrap-wrap">
					<div className="mission-image" style={{ backgroundImage: `url(${mission.image})` }} />
					<div className="mission-title" dangerouslySetInnerHTML={{ __html: mission.name || '' }}></div>
				</div>
				<div className={`mission-progress ${getStatusClassName(mission)}`}>
					<div className="progress-bar">
						<div className="progress-fill" style={{ width: `${mission.progress}%` }}></div>
					</div>
				</div>
			</div>
			<div className="mission-detail-content">
				{mission.tasks.length > 0 && (
					<div className="tasks">
						<div className="title">Tasks </div>
						{mission.tasks?.map((task, index) => {
							const pct = task.execution_count_actual && task.execution_count_expected && (task.execution_count_actual / task.execution_count_expected) * 100
							return (
								<div key={index} className={`task`}>
									<div
										className={`progress-border`}
										style={{
											background: `conic-gradient(from 0deg, ${task.is_completed ? '#1DB01A' : '#E39B65'} 0%, ${
												task.is_completed ? '#C8FACD' : '#F4CE3E'
											} ${pct}%, #7B75C2 ${pct}%, #7B75C2 100%)`,
										}}
									>
										<div className={`counter`}>{task.execution_count_actual}</div>
									</div>
									<div className="task-title">{task.name}</div>
									<div className="task-reward">
										<span className="icon"></span>
										{task.points_reward}
									</div>
								</div>
							)
						})}
					</div>
				)}
				<div className="reward-block">
					<div className="title">Reward</div>
					<div className={`reward`}>{mission.reward}</div>
				</div>
				{needOptin && (
					<div
						className={`btn ${isCompletedMission ? 'disabled' : ''}`}
						onClick={async (e) => {
							e.stopPropagation()
							if (mission.id !== undefined) {
								;(await Smartico.apiReady).requestMissionOptIn(mission.id)
							}
						}}
					>
						Opt-in
					</div>
				)}
				{!needOptin && !isLocked && !isCompletedMission && cta_action && cta_text && (
					<div className={`deposit-btn`} onClick={() => Smartico.dp(cta_action)}>
						{cta_text}
					</div>
				)}
				{mission.related_games && mission.related_games.length > 0 && (
					<SectionWrapper title={getTexts('related_games')}>
						{mission.related_games.map((game, gameIndex) => (
							<div className="related-game" style={{ backgroundImage: `url('${game.game_public_meta.image}')` }} key={gameIndex}>
								<div className="game-overlay">
									<Link to={`/game/${game.ext_game_id}`}>
										<div className="btn">{getTexts('play')}</div>
									</Link>
								</div>
							</div>
						))}
					</SectionWrapper>
				)}
			</div>
		</div>
	)
}

const TournamentsTab = ({ filteredTournaments, onSelectTournament }: { filteredTournaments: any[]; onSelectTournament: (tournament: TTournament) => void }) => {
	return (
		<>
			{filteredTournaments.map((tournament) => {
				return (
					<div className="tournament-card" key={tournament.instance_id} onClick={() => onSelectTournament(tournament)}>
						<div className="tournament-content" style={{ backgroundImage: `url('${tournament.image1}')` }}>
							<div className={`tournament-status ${statusClazz(tournament)}`}>{statusClazz(tournament)}</div>
							<div className="overlay"></div>
							{tournament.ribbon && <div className={`ribbon ${tournament.ribbon}`}>{tournament.ribbon}</div>}
							<div className="tournament-title" dangerouslySetInnerHTML={{ __html: tournament.name }}></div>
						</div>
					</div>
				)
			})}
		</>
	)
}

const TournamentDetails = ({ tournament }: { tournament: TTournament }) => {
	const isMobile = Utils.isMobile()
	const dispatch = useAppDispatch()
	const { getTexts } = useTranslation(true)
	const tournamentInstanceId = tournament.instance_id
	const tournamentInfo = useAppSelector((state: RootState) => state.tournamentInfo.details[tournamentInstanceId])
	console.log('tournamentInfo', tournamentInfo)
	console.log('tournament.instance_id', tournament.instance_id)

	useEffect(() => {
		dispatch(fetchTournamentDetails(tournamentInstanceId))
	}, [dispatch, tournamentInstanceId])

	const handleJoinTournament = async () => {
		try {
			await (await Smartico.apiReady).registerInTournament(tournamentInstanceId)

			dispatch(fetchTournamentDetails(tournamentInstanceId))
		} catch (error) {
			console.error('Error joining the tournament:', error)
		}
	}

	return (
		<div className="tournament-detail-container" style={tournamentInfo?.registration_status === TournamentRegistrationStatusName.NOT_REGISTERED && !isMobile ? { paddingBottom: '66px' } : {}}>
			<div className="tournament-card" key={tournament.instance_id}>
				<div className="tournament-content" style={{ backgroundImage: `url('${tournament.image1}')` }}>
					<div className={`tournament-status ${statusClazz(tournament)}`}>{statusClazz(tournament)}</div>
					<div className="overlay"></div>
					{tournament.ribbon && <div className={`ribbon ${tournament.ribbon}`}>{tournament.ribbon}</div>}
					<div className="tournament-title" dangerouslySetInnerHTML={{ __html: tournament.name }}></div>
				</div>
			</div>
			<div className="tournament-detail-content">
				<div className="tournament-rules">
					<div className="tournament-rules-title">{getTexts('more_info')}</div>
					<div className="tournament-rules-text" dangerouslySetInnerHTML={{ __html: tournament.description }}></div>
				</div>
				{tournamentInfo && tournamentInfo.players && (
					<div className="leaderboard">
						<div className="leaderboard-title">{getTexts('leaderboard')}</div>
						<LeaderboardTab players={tournamentInfo.players} prizes={tournamentInfo.prizes || []} currentUser={tournamentInfo.me ? { ...tournamentInfo.me, is_me: true } : undefined} />
					</div>
				)}
				{tournamentInfo?.related_games && tournamentInfo.related_games.length > 0 && (
					<SectionWrapper title={getTexts('related_games')}>
						{tournamentInfo.related_games.map((game, gameIndex) => (
							<div className="related-game" style={{ backgroundImage: `url('${game.game_public_meta.image}')` }} key={gameIndex}>
								<div className="game-overlay">
									<Link to={`/game/${game.ext_game_id}`}>
										<div className="btn">{getTexts('play')}</div>
									</Link>
								</div>
							</div>
						))}
					</SectionWrapper>
				)}
			</div>
			{tournamentInfo?.registration_status === TournamentRegistrationStatusName.NOT_REGISTERED && (
				<div className="join-button-container">
					<div className="btn" onClick={handleJoinTournament}>
						{getTexts('join_tournament')}
					</div>
				</div>
			)}
		</div>
	)
}

const GamePageSidebar = () => {
	const location = useLocation()
	const isMobile = Utils.isMobile()
	const { getTexts } = useTranslation(true)
	const currentGameId = useAppSelector((state: RootState) => state.game.currentGameExtId)
	const missions = useAppSelector((state: RootState) => state.missions.missions)
	const tournaments = useAppSelector((state: RootState) => state.tournaments.tournaments)

	const [relatedItems, setRelatedItems] = useState<GetRelatedAchTourResponse | null>(null)
	const [activeList, setActiveList] = useState<'achievements' | 'tournaments'>('achievements')
	const [selectedMission, setSelectedMission] = useState<TMissionOrBadge | null>(null)
	const [selectedTournament, setSelectedTournament] = useState<TTournament | null>(null)
	const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false) // Sidebar state

	const toggleSidebar = (state: boolean) => {
		isMobile && setIsSidebarOpen(state)
	}

	const filteredMissions = missions.filter((mission) => relatedItems?.achievements.find((r) => r.ach_id === mission.id))
	const filteredTournaments = tournaments.filter((tournament) => relatedItems?.tournaments?.find((r) => r.tournamentId === tournament.tournament_id))

	useEffect(() => {
		if (selectedMission) {
			const updatedMission = missions.find((m) => m.id === selectedMission.id)
			if (updatedMission && updatedMission !== selectedMission) {
				setSelectedMission(updatedMission)
			}
		}
	}, [missions, selectedMission])

	useEffect(() => {
		setSelectedMission(null)
		setSelectedTournament(null)
		toggleSidebar(false)
	}, [location])

	useEffect(() => {
		const loadData = async () => {
			if (currentGameId) {
				;(await Smartico.apiReady).getRelatedItemsForGame(currentGameId).then((result) => {
					setRelatedItems(result)
				})
			} else {
				console.error('ext_id is undefined')
			}
		}
		loadData()
	}, [currentGameId])

	return (
		<div className="game-sidebar" style={isMobile ? (isSidebarOpen ? { width: '271px', marginRight: '54px' } : { width: '54px' }) : {}}>
			{!isMobile && (
				<div className="top-section" style={{ padding: '36px 0' }}>
					<Link className="logo" to="/"></Link>
				</div>
			)}
			<div className="main-section">
				<NavigationTabs
					onBackAch={() => setSelectedMission(null)}
					onBackTour={() => setSelectedTournament(null)}
					activeList={activeList}
					setActiveList={setActiveList}
					missions={filteredMissions}
					tournaments={tournaments}
					isSidebarOpen={isSidebarOpen}
					toggleSidebar={toggleSidebar}
				/>
				<div className={`related-itmes-list ${isSidebarOpen ? 'open' : ''}`}>
					{activeList === 'achievements' &&
						(selectedMission ? (
							<MissionDetails mission={selectedMission} />
						) : (
							<MissionsTab filteredMissions={filteredMissions} onSelectMission={setSelectedMission} />
						))}
					{activeList === 'tournaments' &&
						(selectedTournament ? (
							<TournamentDetails tournament={selectedTournament} />
						) : (
							<TournamentsTab filteredTournaments={filteredTournaments} onSelectTournament={setSelectedTournament} />
						))}
				</div>
			</div>
		</div>
	)
}

export default GamePageSidebar
